import { useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import { ArrowLeft, Download, Mail, Loader2, X, Eye, MousePointerClick, CheckCircle2, AlertTriangle, Euro, Link2 as LinkIcon, ReceiptText } from "lucide-react";

export default function OfferDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const hasInvoicing = user?.is_admin || ["pro", "business"].includes(user?.plan);
  const [invBusy, setInvBusy] = useState(false);
  const { t, i18n } = useTranslation();
  const OFFER_STATUS = {
    draft: { l: t("offerDetail.statusDraft"), c: "bg-[#1E293B] text-[#E2E8F0]" },
    sent: { l: t("offerDetail.statusSent"), c: "bg-[#111C30] text-white" },
    accepted: { l: t("offerDetail.statusAccepted"), c: "bg-[#22C55E] text-white" },
    refused: { l: t("offerDetail.statusRefused"), c: "bg-[#EF4444] text-white" },
  };
  const [offer, setOffer] = useState(null);
  const [loading, setLoading] = useState(true);
  const [emailOpen, setEmailOpen] = useState(false);
  const [emailTo, setEmailTo] = useState("");
  const [emailSubject, setEmailSubject] = useState("");
  const [emailMessage, setEmailMessage] = useState("");
  const [sending, setSending] = useState(false);
  const [events, setEvents] = useState([]);
  const [curBusy, setCurBusy] = useState(false);

  const copyClientLink = () => {
    const link = `${window.location.origin}/oferta/${id}?t=${offer.public_token}`;
    navigator.clipboard.writeText(link).then(
      () => toast.success(t("offerDetail.linkCopied")),
      () => toast.error(t("offerDetail.linkCopyErr"))
    );
  };

  const toggleCurrency = async () => {
    setCurBusy(true);
    try {
      const res = await api.post(`/offers/${id}/currency`, { show_eur: !offer.show_eur });
      setOffer(res.data);
      toast.success(res.data.show_eur ? t("offerDetail.eurOn") : t("offerDetail.ronOnly"));
    } catch (e) {
      toast.error(e.response?.data?.detail || t("offerDetail.bnrUnavailable"));
    } finally {
      setCurBusy(false);
    }
  };

  const emitInvoice = async () => {
    setInvBusy(true);
    try {
      const r = await api.post(`/invoices/from-offer/${id}?kind=factura`);
      toast.success(t("offerDetail.invoiceCreated"));
      nav(`/app/facturare/${r.data.id}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || t("common.error"));
    } finally {
      setInvBusy(false);
    }
  };

  const load = () => {
    api.get(`/offers/${id}`).then(r => {
      setOffer(r.data);
      setEmailSubject(t("offerDetail.emailSubjectTpl", { number: r.data.offer_number, title: r.data.project_title }));
    }).catch(()=>toast.error(t("offerDetail.offerNotFound"))).finally(()=>setLoading(false));
    api.get(`/offers/${id}/email-events`).then(r => setEvents(r.data.logs || [])).catch(()=>{});
  };
  useEffect(load, [id]);

  const downloadPdf = async () => {
    try {
      const res = await api.get(`/offers/${id}/pdf?lang=${i18n.language}`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `${offer?.offer_number || "oferta"}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => window.URL.revokeObjectURL(url), 1000);
    } catch (e) {
      toast.error(t("offerDetail.pdfError"));
    }
  };

  const sendEmail = async (e) => {
    e.preventDefault();
    if (!emailTo) return;
    setSending(true);
    try {
      await api.post(`/offers/${id}/send-email`, { to_email: emailTo, subject: emailSubject, message: emailMessage, origin_url: window.location.origin, lang: i18n.language });
      toast.success(t("offerDetail.emailSent", { to: emailTo }));
      setEmailOpen(false);
      setEmailTo("");
      setEmailMessage("");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || t("offerDetail.sendError"));
    } finally {
      setSending(false);
    }
  };

  if (loading) return <div className="p-10 text-sm text-[#94A3B8]">{t("offerDetail.loading")}</div>;
  if (!offer) return <div className="p-10">{t("offerDetail.offerNotFound")}</div>;

  return (
    <div className="p-6 lg:p-10 max-w-5xl mx-auto" data-testid="offer-detail">
      <Link to="/app/oferte" className="text-sm text-[#94A3B8] hover:text-[#E2E8F0] flex items-center gap-1"><ArrowLeft size={14}/> {t("offerDetail.back")}</Link>
      <div className="mt-6 flex items-start justify-between flex-wrap gap-4">
        <div>
          <div className="font-mono-plex text-sm text-[#00BFFF]">{offer.offer_number}</div>
          <h1 className="font-display text-3xl lg:text-4xl font-extrabold tracking-tight mt-2">{offer.project_title}</h1>
          <p className="text-sm text-[#94A3B8] mt-1">{t("offerDetail.client")}: <b>{offer.client_name}</b> {offer.client_company && <>· {offer.client_company}</>}</p>
          <div className="mt-3 flex items-center gap-2 flex-wrap">
            <span className={`font-mono-plex text-[11px] px-2 py-0.5 ${OFFER_STATUS[offer.status]?.c || OFFER_STATUS.draft.c}`} data-testid="offer-status">
              {OFFER_STATUS[offer.status]?.l || t("offerDetail.statusDraft")}
            </span>
            {offer.status === "accepted" && offer.job_id && (
              <Link to={`/app/lucrari/${offer.job_id}`} className="text-xs text-[#22C55E] font-semibold underline" data-testid="goto-job-link">
                {t("offerDetail.gotoJob")}
              </Link>
            )}
            {offer.public_token && (
              <button onClick={copyClientLink} className="text-xs text-[#94A3B8] hover:text-[#00BFFF] flex items-center gap-1" data-testid="copy-client-link">
                <LinkIcon size={13}/> {t("offerDetail.copyLink")}
              </button>
            )}
          </div>
          {offer.last_sent_to && (
            <p className="text-xs text-[#22C55E] mt-2" data-testid="last-sent">
              {t("offerDetail.sentOn", { to: offer.last_sent_to, date: offer.last_sent_at?.slice(0,10) })}
            </p>
          )}
        </div>
        <div className="flex gap-2 flex-wrap">
          <button onClick={toggleCurrency} disabled={curBusy} className="btn-outline flex items-center gap-2 disabled:opacity-50" data-testid="toggle-currency-btn">
            {curBusy ? <Loader2 size={18} className="animate-spin"/> : <Euro size={18}/>}
            {offer.show_eur ? t("offerDetail.showRon") : t("offerDetail.convertEur")}
          </button>
          <button onClick={()=>setEmailOpen(true)} className="btn-ink flex items-center gap-2" data-testid="open-send-email-btn">
            <Mail size={18}/> {t("offerDetail.sendEmail")}
          </button>
          {hasInvoicing && offer.status === "accepted" && (
            <button onClick={emitInvoice} disabled={invBusy} className="btn-ink flex items-center gap-2 disabled:opacity-50" data-testid="emit-invoice-btn">
              {invBusy ? <Loader2 size={18} className="animate-spin"/> : <ReceiptText size={18}/>} {t("offerDetail.emitInvoice")}
            </button>
          )}
          <button onClick={downloadPdf} className="btn-primary flex items-center gap-2" data-testid="download-pdf-btn">
            <Download size={18}/> {t("offerDetail.downloadPdf")}
          </button>
        </div>
      </div>

      {/* Email Tracking */}
      {events.length > 0 && (
        <div className="card-flat p-6 mt-8" data-testid="email-tracking">
          <div className="overline mb-4">{t("offerDetail.emailTracking")}</div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-px bg-[#1E293B] border border-[#1E293B]">
            {(() => {
              const totals = events.reduce((acc, e) => {
                acc.delivered += (e.counts?.email_delivered || 0);
                acc.opened += (e.counts?.email_opened || 0);
                acc.clicked += (e.counts?.email_clicked || 0);
                acc.bounced += (e.counts?.email_bounced || 0);
                return acc;
              }, { delivered: 0, opened: 0, clicked: 0, bounced: 0 });
              const items = [
                { icon: CheckCircle2, label: t("offerDetail.delivered"), val: totals.delivered, color: "#22C55E" },
                { icon: Eye, label: t("offerDetail.opened"), val: totals.opened, color: "#00BFFF" },
                { icon: MousePointerClick, label: t("offerDetail.clicked"), val: totals.clicked, color: "#111C30" },
                { icon: AlertTriangle, label: t("offerDetail.bounced"), val: totals.bounced, color: "#EF4444" },
              ];
              return items.map(i => (
                <div key={i.label} className="bg-[#0B111D] p-4">
                  <i.icon size={18} style={{color: i.color}}/>
                  <div className="overline mt-2">{i.label}</div>
                  <div className="font-display text-2xl font-extrabold mt-1">{i.val}</div>
                </div>
              ));
            })()}
          </div>
          <div className="mt-5 space-y-2 text-xs text-[#94A3B8]">
            {events.slice(0, 5).map((ev, i) => (
              <div key={i} className="flex flex-wrap items-center gap-2 border-b border-[#1E293B] pb-2 last:border-0">
                <span className="font-mono-plex text-[#E2E8F0]">{(ev.sent_at || "").slice(0,16).replace("T", " ")}</span>
                <span>→ {ev.to_email}</span>
                {ev.last_event && <span className="ml-auto font-mono-plex text-[#00BFFF] uppercase">{ev.last_event}</span>}
              </div>
            ))}
          </div>
        </div>
      )}

      {offer.ai_intro && (
        <div className="card-flat p-6 mt-8">
          <div className="overline mb-3">{t("offerDetail.intro")}</div>
          <p className="text-sm leading-relaxed">{offer.ai_intro}</p>
        </div>
      )}

      <div className="card-flat p-6 mt-6">
        <div className="overline mb-3">{t("offerDetail.offerLines")}</div>
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-[#1E293B] text-left text-xs uppercase tracking-wider text-[#94A3B8]">
              <th className="py-2">{t("offerDetail.thDesc")}</th>
              <th className="py-2">{t("offerDetail.thCat")}</th>
              <th className="py-2 text-right">{t("offerDetail.thQty")}</th>
              <th className="py-2">{t("offerDetail.thUnit")}</th>
              <th className="py-2 text-right">{t("offerDetail.thUnitPrice")}</th>
              <th className="py-2 text-right">{t("offerDetail.thTotal")}</th>
            </tr>
          </thead>
          <tbody>
            {offer.items.map((it, i) => (
              <tr key={i} className="border-b border-[#1E293B]">
                <td className="py-3 pr-2">{it.description}</td>
                <td className="py-3 pr-2 text-[#94A3B8]">{it.category}</td>
                <td className="py-3 pr-2 text-right font-mono-plex">{it.quantity.toFixed(2)}</td>
                <td className="py-3 pr-2">{it.unit}</td>
                <td className="py-3 pr-2 text-right font-mono-plex">{it.unit_price.toFixed(2)}</td>
                <td className="py-3 pr-2 text-right font-mono-plex font-semibold">{(it.quantity * it.unit_price).toFixed(2)}</td>
              </tr>
            ))}
          </tbody>
        </table>
        <div className="mt-6 max-w-sm ml-auto space-y-1 text-sm" data-testid="offer-totals">
          {(() => {
            const rate = offer.eur_rate;
            const showEur = offer.show_eur && rate;
            const eur = (v) => `${(v / rate).toFixed(2)} €`;
            return (
              <>
                <div className="flex justify-between"><span className="text-[#94A3B8]">{t("offerDetail.subtotal")}</span><span className="font-mono-plex">{offer.totals.subtotal.toFixed(2)} RON{showEur && <span className="text-[#94A3B8]"> · {eur(offer.totals.subtotal)}</span>}</span></div>
                <div className="flex justify-between"><span className="text-[#94A3B8]">{t("offerDetail.markup")} ({offer.markup_percent}%)</span><span className="font-mono-plex">{offer.totals.markup.toFixed(2)} RON{showEur && <span className="text-[#94A3B8]"> · {eur(offer.totals.markup)}</span>}</span></div>
                <div className="flex justify-between font-semibold border-t border-[#1E293B] pt-2"><span>{t("offerDetail.base")}</span><span className="font-mono-plex">{offer.totals.base.toFixed(2)} RON{showEur && <span className="text-[#94A3B8] font-normal"> · {eur(offer.totals.base)}</span>}</span></div>
                <div className="flex justify-between"><span className="text-[#94A3B8]">{t("offerDetail.vat")} ({offer.vat_percent}%)</span><span className="font-mono-plex">{offer.totals.vat.toFixed(2)} RON{showEur && <span className="text-[#94A3B8]"> · {eur(offer.totals.vat)}</span>}</span></div>
                <div className="bg-[#00BFFF] text-white p-3 flex justify-between items-center mt-3">
                  <span className="font-display font-bold">{t("offerDetail.total")}</span>
                  <span className="font-display font-extrabold text-xl" data-testid="offer-total-value">
                    {offer.totals.total.toFixed(2)} RON{showEur && <span className="text-base font-bold"> · {eur(offer.totals.total)}</span>}
                  </span>
                </div>
                {showEur && (
                  <p className="text-xs text-[#94A3B8] pt-2 text-right" data-testid="bnr-rate-note">
                    {t("offerDetail.bnrNote", { date: offer.eur_rate_date, rate: rate.toFixed(4) })}
                  </p>
                )}
              </>
            );
          })()}
        </div>
      </div>

      {offer.notes && (
        <div className="card-flat p-6 mt-6">
          <div className="overline mb-3">{t("offerDetail.notes")}</div>
          <p className="text-sm whitespace-pre-wrap">{offer.notes}</p>
        </div>
      )}

      {/* Email Modal */}
      {emailOpen && (
        <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" data-testid="email-modal" onClick={()=>!sending && setEmailOpen(false)}>
          <form onSubmit={sendEmail} onClick={(e)=>e.stopPropagation()} className="bg-[#0B111D] border border-[#1E293B] w-full max-w-lg">
            <div className="flex items-center justify-between p-5 border-b border-[#1E293B]">
              <div>
                <div className="overline">{t("offerDetail.sendOffer")}</div>
                <h2 className="font-display text-xl font-bold mt-1">{t("offerDetail.emailPdfAttached")}</h2>
              </div>
              <button type="button" onClick={()=>setEmailOpen(false)} className="p-2 hover:bg-[#0B111D]" data-testid="close-email-modal"><X size={18}/></button>
            </div>
            <div className="p-5 space-y-3">
              <div>
                <label className="overline block mb-2">{t("offerDetail.to")}</label>
                <input type="email" required value={emailTo} onChange={(e)=>setEmailTo(e.target.value)} placeholder={t("offerDetail.toPh")} className="input-flat" data-testid="email-to"/>
              </div>
              <div>
                <label className="overline block mb-2">{t("offerDetail.subject")}</label>
                <input value={emailSubject} onChange={(e)=>setEmailSubject(e.target.value)} className="input-flat" data-testid="email-subject"/>
              </div>
              <div>
                <label className="overline block mb-2">{t("offerDetail.message")}</label>
                <textarea rows={4} value={emailMessage} onChange={(e)=>setEmailMessage(e.target.value)} placeholder={t("offerDetail.messagePh")} className="input-flat" data-testid="email-message"/>
              </div>
              <div className="text-xs text-[#94A3B8] bg-[#0B111D] p-3 border-l-2 border-[#00BFFF]">
                {t("offerDetail.emailHint")}
              </div>
            </div>
            <div className="flex justify-end gap-2 p-5 border-t border-[#1E293B]">
              <button type="button" onClick={()=>setEmailOpen(false)} disabled={sending} className="btn-outline">{t("offerDetail.cancel")}</button>
              <button type="submit" disabled={sending} className="btn-primary flex items-center gap-2 disabled:opacity-50" data-testid="send-email-btn">
                {sending ? <><Loader2 size={16} className="animate-spin"/> {t("offerDetail.sending")}</> : <><Mail size={16}/> {t("offerDetail.send")}</>}
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}
